import {
    Platform,
    StyleSheet,
    Text,
    View,
} from 'react-native';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import colors from '../../lib/colors';
import { interMedium, interRegular, interSemiBold } from '../../lib/fonts';
import type {
    PricingAccountProjectContext,
    PricingCatalogPlan,
} from '../../lib/pricingCatalog';
import { resolveMessage } from '../../lib/i18n/messages';

type EarlyUserProjectSummaryProps = {
    context: PricingAccountProjectContext;
    recommendedPlan: PricingCatalogPlan | null;
    fontsLoaded: boolean;
};

export function EarlyUserProjectSummary({
    context,
    recommendedPlan,
    fontsLoaded,
}: EarlyUserProjectSummaryProps) {
    const { i18n } = useLingui();
    const pausedProjectCount = context.paused_project_count ?? 0;

    return (
        <View style={styles.summary} testID="early-user-project-summary">
            <View style={styles.summaryCount}>
                <Text style={[styles.summaryCountNumber, fontsLoaded && interSemiBold]}>
                    {context.active_project_count}
                </Text>
                <Text style={[styles.summaryCountLabel, fontsLoaded && interMedium]}>
                    {resolveMessage(i18n, msg({
                        id: 'pricing.earlyUser.activeProjectCountShort',
                        message: '{count, plural, one {active project} other {active projects}}',
                    }), { count: context.active_project_count })}
                </Text>
            </View>
            <View style={styles.summaryCopy}>
                <Text style={[styles.summaryTitle, fontsLoaded && interSemiBold]}>
                    {resolveMessage(i18n, msg({
                        id: 'pricing.earlyUser.activeProjectCount',
                        message: 'Your current account uses {count, plural, one {# active project} other {# active projects}}.',
                    }), { count: context.active_project_count })}
                </Text>
                <Text style={[styles.summaryDescription, fontsLoaded && interRegular]}>
                    {pausedProjectCount > 0
                        ? resolveMessage(i18n, msg({
                            id: 'pricing.earlyUser.pausedProjects',
                            message: '{count, plural, one {# project remains paused until you resume it manually} other {# projects remain paused until you resume them manually}}, subject to the selected plan limit.',
                        }), { count: pausedProjectCount })
                        : recommendedPlan
                            ? resolveMessage(i18n, msg({
                                id: 'pricing.earlyUser.recommendedPlan',
                                message: '{plan} is the lowest plan whose active-project limit fits this account.',
                            }), { plan: recommendedPlan.name })
                            : i18n._(msg({
                                id: 'pricing.earlyUser.planImpactExplanation',
                                message: 'Each plan shows which oldest projects remain active and which projects pause.',
                            }))}
                </Text>
            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    summary: {
        alignSelf: 'center',
        width: '100%',
        maxWidth: 760,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 20,
        paddingVertical: 18,
        paddingHorizontal: 4,
        borderBottomWidth: 1,
        borderBottomColor: colors.border,
        marginBottom: 28,
        ...(Platform.OS !== 'web' && { flexWrap: 'wrap' }),
    },
    summaryCount: {
        minWidth: 126,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 7,
    },
    summaryCountNumber: {
        fontSize: 32,
        lineHeight: 38,
        color: colors.darkGreen,
    },
    summaryCountLabel: {
        fontSize: 13,
        color: colors.text500,
        lineHeight: 17,
    },
    summaryCopy: {
        flex: 1,
        minWidth: 220,
    },
    summaryTitle: {
        fontSize: 16,
        lineHeight: 22,
        color: colors.text900,
        marginBottom: 3,
    },
    summaryDescription: {
        fontSize: 14,
        lineHeight: 20,
        color: colors.text500,
    },
});
